import type { BuilderDraft, BuilderElement, BuilderField, FieldWidget, SectionElement } from "@froppio/form-core";
import { widgetLabel } from "@froppio/form-core";

export function elementId(element: BuilderElement) {
  return element.type === "field" ? element.fieldId : element.id;
}

export function findElement(draft: BuilderDraft, id: string): BuilderElement | undefined {
  for (const element of draft.elements) {
    if (elementId(element) === id) return element;
    if (element.type === "section") {
      const field = element.elements.find(child => child.fieldId === id);
      if (field) return field;
    }
  }
}

export function findContainer(draft: BuilderDraft, id: string) {
  return draft.elements.find((element): element is SectionElement =>
    element.type === "section" && element.elements.some(child => child.fieldId === id));
}

export function createField(widget: FieldWidget): BuilderField {
  return {
    id: `field_${crypto.randomUUID()}`, widget, label: widgetLabel(widget),
    helpText: "", required: false, choices: [],
  };
}

export function createSection(): SectionElement {
  return { type: "section", id: `section_${crypto.randomUUID()}`, title: "New section", description: "", elements: [] };
}

export function insertElement(draft: BuilderDraft, element: BuilderElement, sectionId: string | null, index: number): BuilderDraft {
  if (sectionId === null) {
    const elements = [...draft.elements];
    elements.splice(index, 0, element);
    return { ...draft, elements };
  }
  if (element.type === "section") throw new Error("Sections cannot be nested.");
  const section = draft.elements.find(item => item.type === "section" && item.id === sectionId);
  if (!section || section.type !== "section") throw new Error("The destination section is no longer available.");
  const children = [...section.elements];
  children.splice(index, 0, element);
  return { ...draft, elements: draft.elements.map(item => item === section ? { ...section, elements: children } : item) };
}

function detachElement(draft: BuilderDraft, id: string): BuilderDraft {
  return {
    ...draft,
    elements: draft.elements.filter(element => elementId(element) !== id)
      .map(element => element.type === "section"
        ? { ...element, elements: element.elements.filter(child => child.fieldId !== id) } : element),
  };
}

export function moveElement(draft: BuilderDraft, id: string, sectionId: string | null, index: number): BuilderDraft {
  const element = findElement(draft, id);
  if (!element) throw new Error("The selected item is no longer available.");
  const source = findContainer(draft, id);
  const siblings = source?.elements ?? draft.elements;
  const sourceIndex = siblings.findIndex(item => elementId(item) === id);
  const adjustedIndex = (source?.id ?? null) === sectionId && sourceIndex < index ? index - 1 : index;
  return insertElement(detachElement(draft, id), element, sectionId, adjustedIndex);
}

export function moveElementBy(draft: BuilderDraft, id: string, direction: -1 | 1): BuilderDraft {
  const container = findContainer(draft, id);
  const siblings = container?.elements ?? draft.elements;
  const index = siblings.findIndex(item => elementId(item) === id);
  if (index < 0 || index + direction < 0 || index + direction >= siblings.length) {
    throw new Error("This item cannot move further in that direction.");
  }
  return moveElement(draft, id, container?.id ?? null, direction === 1 ? index + 2 : index - 1);
}

export function deleteElement(draft: BuilderDraft, id: string): BuilderDraft {
  const element = findElement(draft, id);
  if (!element) throw new Error("The selected item is no longer available.");
  const fields = { ...draft.fields };
  const fieldIds = element.type === "section" ? element.elements.map(child => child.fieldId) : [id];
  for (const fieldId of fieldIds) delete fields[fieldId];
  return { ...detachElement(draft, id), fields };
}

export function updateField(draft: BuilderDraft, field: BuilderField): BuilderDraft {
  if (!draft.fields[field.id]) throw new Error("The selected question is no longer available.");
  return { ...draft, fields: { ...draft.fields, [field.id]: field } };
}

export function updateSection(draft: BuilderDraft, section: SectionElement): BuilderDraft {
  if (!draft.elements.some(element => element.type === "section" && element.id === section.id)) {
    throw new Error("The selected section is no longer available.");
  }
  return { ...draft, elements: draft.elements.map(element => elementId(element) === section.id ? section : element) };
}
